import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { Search, User, Mail, Phone, Calendar, ChevronDown, ChevronUp, RefreshCw, Download, Globe, MapPin } from 'lucide-react';
import { toast } from 'react-hot-toast';

const MyDirects = () => {
  const [directsData, setDirectsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [userSponsorId, setUserSponsorId] = useState('');
  const [userDbKey, setUserDbKey] = useState('');
  const [userRegDate, setUserRegDate] = useState('');

  // Get user data from localStorage
  useEffect(() => {
    const sponsorId = localStorage.getItem('userSponsorId');
    const dbKey = localStorage.getItem('dbKey');
    const regDate = localStorage.getItem('registrationDate');
    
    if (sponsorId && dbKey && regDate) {
      setUserSponsorId(sponsorId);
      setUserDbKey(dbKey);
      setUserRegDate(regDate);
      fetchDirects(dbKey, regDate);
    } else {
      toast.error('User data not found');
      setLoading(false);
    }
  }, []);

  // Fetch ONLY direct referrals from user's referrals array
  const fetchDirects = async (dbKey, regDate) => {
    try {
      setLoading(true);
      
      // Get current user's data
      const userRef = ref(database, `users/${regDate}/${dbKey}`);
      const snapshot = await get(userRef);
      
      if (!snapshot.exists()) {
        console.log('User not found');
        setDirectsData([]);
        setLoading(false);
        return;
      }

      const userData = snapshot.val();
      console.log('Current user:', userData.sponsorId);
      console.log('Referrals array:', userData.referrals);
      
      const referrals = userData.referrals || [];
      
      if (!referrals.length) {
        console.log('No referrals found');
        setDirectsData([]);
        setFilteredData([]);
        setLoading(false);
        return;
      }

      // Get all users to find complete details for each referral
      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      
      if (!usersSnapshot.exists()) {
        setDirectsData([]);
        setLoading(false);
        return;
      }

      const allUsers = usersSnapshot.val();
      const directs = [];

      const getBestLocation = (user) => {
        const loginLoc = user?.lastLoginLocation || null;
        const regLoc = user?.registrationLocation || null;
        const pick = (loc) =>
          loc && (loc.city || loc.region || loc.country || loc.ipAddress);

        const chosen = pick(loginLoc) ? loginLoc : regLoc;
        return chosen || null;
      };

      // Process each referral from the array
      for (const refItem of referrals) {
        console.log('Looking for referral:', refItem);
        
        let foundUser = null;
        let foundKey = null;
        let foundDate = null;

        // Search through date structure
        for (const [date, dateGroup] of Object.entries(allUsers)) {
          if (typeof dateGroup === 'object' && dateGroup !== null) {
            for (const [key, user] of Object.entries(dateGroup)) {
              // Match by dbKey first (most reliable)
              if (refItem.dbKey && key === refItem.dbKey) {
                foundUser = user;
                foundKey = key;
                foundDate = date;
                break;
              }
              // Then match by sponsorId
              if (user.sponsorId === refItem.sponsorId) {
                foundUser = user;
                foundKey = key;
                foundDate = date;
                break;
              }
            }
          }
          if (foundUser) break;
        }

        // If found, add to directs list
        if (foundUser) {
          const bestLocation = getBestLocation(foundUser);
          const ipAddress =
            bestLocation?.ipAddress ||
            foundUser.ipAddress ||
            foundUser.lastLoginIP ||
            'N/A';
          const country =
            bestLocation?.country ||
            foundUser.country ||
            'Unknown';
          const location =
            [bestLocation?.city, bestLocation?.region]
              .filter(Boolean)
              .join(', ') || country;

          directs.push({
            id: foundKey,
            name: foundUser.name || 'Unknown',
            memberId: foundUser.sponsorId || 'N/A',
            email: foundUser.email || 'N/A',
            mobile: foundUser.mobile || 'N/A',
            joinDate: foundUser.registrationDate || foundDate || new Date().toISOString(),
            status: foundUser.status === 'active' ? 'Active' : 'Inactive',
            package: foundUser.totalInvestment || 0,
            directs: foundUser.referrals?.length || 0,
            business: foundUser.totalInvestment || 0,
            country,
            ipAddress,
            location
          });
        }
      }

      console.log('Found directs:', directs.length);
      setDirectsData(directs);
      setFilteredData(directs);
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load directs');
    } finally {
      setLoading(false);
    }
  };

  // Filter based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(directsData);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = directsData.filter(item => 
      item.name.toLowerCase().includes(term) ||
      item.memberId.toLowerCase().includes(term) ||
      item.email.toLowerCase().includes(term) ||
      item.mobile.includes(term)
    );
    
    setFilteredData(filtered);
  }, [searchTerm, directsData]);

  // Format helpers
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-IN', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amt) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', 
      currency: 'USD', 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amt || 0);
  };

  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const refreshData = () => {
    if (userDbKey && userRegDate) {
      fetchDirects(userDbKey, userRegDate);
      toast.success('Refreshing...');
    }
  };

  const exportToCSV = () => {
    if (!filteredData.length) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Member ID', 'Name', 'Email', 'Mobile', 'Join Date', 'Status', 'Package', 'Directs', 'Country', 'IP Address', 'Location'];
    const rows = filteredData.map(d => [
      d.memberId, d.name, d.email, d.mobile, 
      formatDate(d.joinDate), d.status, d.package, d.directs, d.country, d.ipAddress, d.location
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my-directs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported successfully');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        <span className="ml-3 text-foreground">Loading your direct team...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">My Direct Team</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Your Sponsor ID: <span className="font-bold text-primary">{userSponsorId}</span>
          </p>
          <p className="text-muted-foreground text-sm">
            Total Directs: <span className="font-bold text-foreground">{directsData.length}</span>
          </p>
        </div>
        <button
          onClick={refreshData}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary/20 hover:bg-primary/30 text-primary rounded-full transition"
        >
          <RefreshCw size={18} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="card-glow p-4 rounded-xl">
          <div className="text-muted-foreground text-xs mb-1">Total Directs</div>
          <div className="text-2xl font-bold text-foreground">{directsData.length}</div>
        </div>
        <div className="card-glow p-4 rounded-xl">
          <div className="text-muted-foreground text-xs mb-1">Active Members</div>
          <div className="text-2xl font-bold text-foreground">
            {directsData.filter(d => d.status === 'Active').length}
          </div>
        </div>
      </div>

      {/* Search & Export */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary h-4 w-4" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name, ID, email..."
            className="w-full pl-9 pr-4 py-3 bg-card/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:border-primary outline-none text-sm"
          />
        </div>
        <button
          onClick={exportToCSV}
          disabled={!filteredData.length}
          className={`flex items-center justify-center gap-2 px-5 py-3 rounded-lg transition ${
            filteredData.length 
              ? 'btn-electric text-primary-foreground' 
              : 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
          }`}
        >
          <Download size={18} />
          <span>Export</span>
        </button>
      </div>

      {/* Direct Members List */}
      {filteredData.length === 0 ? (
        <div className="text-center py-12 bg-card/30 rounded-xl">
          <div className="text-5xl mb-4">👥</div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Direct Members</h3>
          <p className="text-muted-foreground text-sm">
            {directsData.length === 0 
              ? "You haven't referred anyone yet. Share your referral link to start building your team!"
              : "No members match your search criteria."}
          </p>
          <button
            onClick={refreshData}
            className="mt-4 px-6 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition"
          >
            Refresh
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredData.map((member) => (
            <div key={member.id} className="bg-card/60 border border-border rounded-xl overflow-hidden">
              {/* Header - Always Visible */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer"
                onClick={() => toggleExpand(member.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-lg font-bold text-foreground">
                    {getInitials(member.name)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{member.name}</h3>
                    <p className="text-xs text-muted-foreground">ID: {member.memberId}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        member.status === 'Active' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {member.status}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {member.directs} Directs
                      </span>
                    </div>
                  </div>
                </div>
                {expandedId === member.id ? 
                  <ChevronUp className="h-5 w-5 text-primary" /> : 
                  <ChevronDown className="h-5 w-5 text-primary" />
                }
              </div>

              {/* Expanded Details */}
              {expandedId === member.id && (
                <div className="px-4 pb-4 border-t border-border">
                  {/* Contact Info */}
                  <div className="grid grid-cols-1 gap-3 py-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-primary" />
                      <span className="text-foreground">{member.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-primary" />
                      <span className="text-foreground">{member.mobile}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span className="text-foreground">Joined: {formatDate(member.joinDate)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="h-4 w-4 text-primary" />
                      <span className="text-foreground">Country: {member.country || 'Unknown'}</span>
                    </div>
                    {/* <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="text-foreground">Location: {member.location || 'N/A'}</span>
                    </div> */}
                    {/* <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-primary" />
                      <span className="text-foreground">IP: {member.ipAddress || 'N/A'}</span>
                    </div> */}
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div className="bg-card/40 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground">Package</div>
                      <div className="font-bold text-foreground">{formatCurrency(member.package)}</div>
                    </div>
                    <div className="bg-card/40 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground">Total Business</div>
                      <div className="font-bold text-foreground">{formatCurrency(member.business)}</div>
                    </div>
                  </div>

                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyDirects;

